import React, { useEffect, useState } from "react";
import {
  useSendAssistantBotMessageMutation,
  useStartAssistantBotSessionMutation,
} from "actions/assistantBot/mutations";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RiCloseLine, RiRobotLine, RiSendPlaneFill, RiRefreshLine } from "react-icons/ri";
import { useParams } from "react-router-dom";

const FloatingAssistantBot = () => {
  const { clientID: dossierId } = useParams();
  const [opened, setOpened] = useState(false);
  const [fabPos, setFabPos] = useState({ x: 0, y: 0 });
  const [chatPos, setChatPos] = useState({ x: 0, y: 0 });
  const [sessionId, setSessionId] = useState("");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);

  const [startSession] = useStartAssistantBotSessionMutation();
  const [sendMessage, { isLoading: isSending }] = useSendAssistantBotMessageMutation();

  const [dragState, setDragState] = useState({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
  });

  const [chatDragState, setChatDragState] = useState({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
  });

  const beginNewSession = async () => {
    if (!dossierId) return;
    setIsStartingSession(true);
    setErrorText("");
    try {
      const response = await startSession({
        dossier_client_id: dossierId,
        force_new: true,
      }).unwrap();
      if (response?.session_id) {
        setSessionId(response.session_id);
        setMessages(Array.isArray(response?.messages) ? response.messages : []);
      }
    } catch (error) {
      setErrorText("Impossible de demarrer la session IA.");
      setSessionId("");
      setMessages([]);
    } finally {
      setIsStartingSession(false);
    }
  };

  const endSession = () => {
    setSessionId("");
    setMessages([]);
    setInput("");
    setErrorText("");
    setIsBotThinking(false);
    setChatPos({ x: 0, y: 0 });
  };

  const handleOpen = async () => {
    setOpened(true);
    await beginNewSession();
  };

  const handleClose = () => {
    setOpened(false);
    endSession();
  };

  const handleResetChat = async () => {
    setInput("");
    setErrorText("");
    setIsBotThinking(false);
    await beginNewSession();
  };

  useEffect(() => {
    const initX = Math.max(12, window.innerWidth - 76);
    const initY = Math.max(12, window.innerHeight - 99);
    setFabPos((prev) => {
      if (prev.x || prev.y) return prev;
      return { x: initX, y: initY };
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setFabPos((prev) => {
        const maxX = Math.max(12, window.innerWidth - 68);
        const maxY = Math.max(12, window.innerHeight - 68);
        return {
          x: Math.min(Math.max(12, prev.x), maxX),
          y: Math.min(Math.max(12, prev.y), maxY),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const beginDrag = (event) => {
    const isTouch = event.touches && event.touches.length > 0;
    const point = isTouch ? event.touches[0] : event;
    setDragState({
      dragging: true,
      startX: point.clientX,
      startY: point.clientY,
      originX: fabPos.x,
      originY: fabPos.y,
      moved: false,
    });
  };

  useEffect(() => {
    if (!dragState.dragging) return undefined;

    const onMove = (event) => {
      const isTouch = event.touches && event.touches.length > 0;
      const point = isTouch ? event.touches[0] : event;
      const deltaX = point.clientX - dragState.startX;
      const deltaY = point.clientY - dragState.startY;
      const maxX = Math.max(12, window.innerWidth - 68);
      const maxY = Math.max(12, window.innerHeight - 68);
      setFabPos({
        x: Math.min(Math.max(12, dragState.originX + deltaX), maxX),
        y: Math.min(Math.max(12, dragState.originY + deltaY), maxY),
      });
      if (!dragState.moved && (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4)) {
        setDragState((prev) => ({ ...prev, moved: true }));
      }
    };

    const onUp = () => {
      setDragState((prev) => ({ ...prev, dragging: false }));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragState, fabPos.x, fabPos.y]);

  const beginChatDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isTouch = event.touches && event.touches.length > 0;
    const point = isTouch ? event.touches[0] : event;
    setChatDragState({
      dragging: true,
      startX: point.clientX,
      startY: point.clientY,
      originX: chatPos.x,
      originY: chatPos.y,
      moved: false,
    });
  };

  useEffect(() => {
    if (!chatDragState.dragging) return undefined;

    const onMove = (event) => {
      event.preventDefault();
      const isTouch = event.touches && event.touches.length > 0;
      const point = isTouch ? event.touches[0] : event;
      const deltaX = point.clientX - chatDragState.startX;
      const deltaY = point.clientY - chatDragState.startY;

      const chatWidth = 360;
      const chatHeight = 480;
      const maxX = Math.max(-chatWidth + 100, window.innerWidth - chatWidth);
      const maxY = Math.max(-chatHeight + 50, window.innerHeight - chatHeight);
      const minX = -chatWidth + 100;
      const minY = -50;

      setChatPos({
        x: Math.min(Math.max(minX, chatDragState.originX + deltaX), maxX),
        y: Math.min(Math.max(minY, chatDragState.originY + deltaY), maxY),
      });

      if (!chatDragState.moved && (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4)) {
        setChatDragState((prev) => ({ ...prev, moved: true }));
      }
    };

    const onUp = () => {
      setChatDragState((prev) => ({ ...prev, dragging: false }));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [chatDragState, chatPos.x, chatPos.y]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !sessionId || isSending) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { id: `temp-user-${Date.now()}`, sender: "user", content: text },
    ]);
    setIsBotThinking(true);
    try {
      setErrorText("");
      const response = await sendMessage({ sessionId, message: text }).unwrap();
      const botMsg = response?.bot_message;
      setMessages((prev) => [...prev, ...(botMsg ? [botMsg] : [])]);
    } catch (error) {
      setErrorText("Erreur d'envoi. Reessayez.");
    } finally {
      setIsBotThinking(false);
    }
  };

  if (!dossierId) return null;

  return (
    <>
      <style>
        {`
          @keyframes assistantBotDotPulse {
            0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
            40% { opacity: 1; transform: translateY(-2px); }
          }
        `}
      </style>
      <button
        type="button"
        onMouseDown={beginDrag}
        onTouchStart={beginDrag}
        onClick={() => {
          if (dragState.moved) {
            setDragState((prev) => ({ ...prev, moved: false }));
            return;
          }
          if (opened) {
            handleClose();
          } else {
            handleOpen();
          }
        }}
        style={{
          position: "fixed",
          left: fabPos.x,
          top: fabPos.y,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: "none",
          background: "#009688",
          color: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          zIndex: 1100,
          fontSize: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: "none",
          cursor: dragState.dragging ? "grabbing" : "grab",
        }}
      >
        {opened ? <RiCloseLine /> : <RiRobotLine />}
      </button>

      {opened && (
        <div
          style={{
            position: "fixed",
            right: chatPos.x === 0 ? 12 : "auto",
            bottom: chatPos.y === 0 ? 111 : "auto",
            left: chatPos.x !== 0 ? `calc(50% + ${chatPos.x}px)` : "auto",
            top: chatPos.y !== 0 ? `calc(50% + ${chatPos.y}px)` : "auto",
            transform: chatPos.x !== 0 || chatPos.y !== 0 ? "translate(-50%, -50%)" : "none",
            width: "min(360px, calc(100vw - 24px))",
            height: "min(480px, calc(100vh - 140px))",
            maxHeight: "calc(100vh - 140px)",
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e9ecef",
            boxShadow: "0 12px 28px rgba(0,0,0,0.22)",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: chatDragState.dragging ? "none" : "all 0.2s ease",
          }}
        >
          <div
            onMouseDown={beginChatDrag}
            onTouchStart={beginChatDrag}
            style={{
              background: "#009688",
              color: "#fff",
              padding: "10px 12px",
              fontWeight: 600,
              cursor: chatDragState.dragging ? "grabbing" : "grab",
              userSelect: "none",
              touchAction: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Assistant OHADA / SYSCOHADA</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleResetChat();
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              title="Nouvelle discussion"
            >
              <RiRefreshLine />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 10, background: "#f8f9fa" }}>
            {isStartingSession && <div style={{ color: "#6c757d" }}>Chargement...</div>}
            {errorText && <div style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>{errorText}</div>}
            {!isStartingSession && messages.map((msg) => {
              const isUser = msg.sender === "user";
              return (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      maxWidth: "85%",
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: isUser ? "#009688" : "#fff",
                      color: isUser ? "#fff" : "#212529",
                      border: isUser ? "none" : "1px solid #e9ecef",
                      fontSize: 13,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {isUser ? (
                      msg.content
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
                        }}
                      >
                        {msg.content || ""}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
              );
            })}
            {isBotThinking && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
                <div
                  style={{
                    maxWidth: "85%",
                    padding: "8px 10px",
                    borderRadius: 10,
                    background: "#fff",
                    color: "#212529",
                    border: "1px solid #e9ecef",
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#6c757d",
                      marginRight: 4,
                      animation: "assistantBotDotPulse 1.2s infinite",
                    }}
                  />
                  <span
                    style={{
                      display: "inline-block",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#6c757d",
                      marginRight: 4,
                      animation: "assistantBotDotPulse 1.2s infinite 0.2s",
                    }}
                  />
                  <span
                    style={{
                      display: "inline-block",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#6c757d",
                      animation: "assistantBotDotPulse 1.2s infinite 0.4s",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, padding: 10, borderTop: "1px solid #e9ecef" }}>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSend();
              }}
              placeholder="Votre message..."
              disabled={isStartingSession || !sessionId}
              style={{
                flex: 1,
                border: "1px solid #ced4da",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 13,
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || isSending || !sessionId || isStartingSession}
              style={{
                border: "none",
                borderRadius: 8,
                width: 38,
                height: 38,
                background: "#009688",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: !input.trim() || isSending || !sessionId || isStartingSession ? 0.6 : 1,
              }}
            >
              <RiSendPlaneFill />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAssistantBot;
